import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import {
  TrashIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { Table } from "../../components/table";
import { Button } from "../../components/button";
import { DeleteConfirm } from "../../components/modal/deleteConfirm";
import { SuccessToast } from "../../components/toast/success";
import { PageHeading } from "../../components/pageHeading";

const StatusBadge = ({ ok, label }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
    }`}
  >
    {label}
  </span>
);

const columns = [
  {
    Header: "Organization",
    accessor: "name",
    Cell: ({ value }) => (
      <span className="font-medium text-gray-900 dark:text-white">{value}</span>
    ),
  },
  {
    Header: "Owner",
    accessor: "email",
    Cell: ({ row }) =>
      row.original.email ? (
        <div>
          <div className="text-gray-900 dark:text-white">
            {row.original.first_name} {row.original.last_name}
          </div>
          <div className="text-gray-500 text-sm">{row.original.email}</div>
        </div>
      ) : (
        <span className="text-red-500 text-sm">No owner user</span>
      ),
  },
  {
    Header: "Initialized",
    accessor: "initialization_complete",
    Cell: ({ value }) => (
      <StatusBadge ok={!!value} label={value ? "Yes" : "No"} />
    ),
  },
  {
    Header: "Stripe",
    accessor: "stripe_customer_id",
    Cell: ({ value }) => (
      <StatusBadge ok={!!value} label={value || "Missing"} />
    ),
  },
  {
    Header: "Kinde org",
    accessor: "auth_org_code",
    Cell: ({ value }) => (
      <StatusBadge ok={!!value} label={value || "Missing"} />
    ),
  },
  {
    Header: "",
    id: "delete",
    accessor: (str) => "delete",
    Cell: ({ row }) => (
      <button
        className="pl-6 py-4 block text-red-500 hover:text-red-600"
        onClick={() => row.original._onDelete(row.original.name, row.original.id)}
      >
        <TrashIcon className="h-4 w-4 shrink-0" />
      </button>
    ),
  },
];

function Superadmin() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState({
    identifier: "",
    id: "",
    secondaryId: "",
  });
  const [deleteSuccess, setDeleteSuccess] = useState({
    displaying: false,
    message: "Organization permanently deleted",
  });
  const [isReconciling, setIsReconciling] = useState(false);
  const [reconcileToast, setReconcileToast] = useState({
    displaying: false,
    message: "",
  });

  const fetchOrgs = async () => {
    setIsLoading(true);
    setFetchError(null);
    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/superadmin/orgs`
      );
      if (res.status === 401) {
        navigate("/auth/login/");
        return;
      }
      const data = await res.json();
      if (res.ok) {
        setOrgs(Array.isArray(data) ? data : []);
      } else {
        setFetchError(`${res.status}: ${data?.error || res.statusText}`);
      }
    } catch (err) {
      setFetchError(err.message);
    } finally {
      await timerPromise;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  const handleDeleteClick = (identifier, id) => {
    setSelectedDeleteItem({ identifier, id, secondaryId: "" });
    setIsDeleteOpen(true);
  };

  // Heal any orgs left with no active user (pulls their owner back from Kinde).
  const reconcileOrgs = async () => {
    setIsReconciling(true);
    setFetchError(null);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/v1/superadmin/orgs/reconcile`,
        { method: "POST" }
      );
      const data = await res.json();
      if (res.ok) {
        await fetchOrgs();
        setReconcileToast({
          displaying: true,
          message: `Reconcile complete: ${data.healed} healed of ${data.scanned} empty account${
            data.scanned === 1 ? "" : "s"
          }`,
        });
        setTimeout(
          () => setReconcileToast((s) => ({ ...s, displaying: false })),
          4000
        );
      } else {
        setFetchError(`${res.status}: ${data?.error || res.statusText}`);
      }
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setIsReconciling(false);
    }
  };

  // Throw on a non-2xx response so DeleteConfirm doesn't treat a failed
  // delete as a success; surface the error in the page banner instead.
  const deleteOrg = async (orgId) => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/v1/superadmin/orgs/${orgId}`,
      { method: "DELETE" }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setIsDeleteOpen(false);
      setFetchError(`Delete failed (${res.status}): ${data?.error || res.statusText}`);
      throw new Error(data?.error || res.statusText);
    }
    return data;
  };

  const handleDeleteSuccess = () => {
    fetchOrgs();
    setDeleteSuccess({ displaying: true, message: "Organization permanently deleted" });
    setTimeout(() => setDeleteSuccess((s) => ({ ...s, displaying: false })), 3000);
  };

  // Inject the delete handler into each row so the static columns definition can call it
  const tableData = orgs.map((org) => ({
    ...org,
    _onDelete: handleDeleteClick,
  }));

  return (
    <div>
      <main className="py-6">
        <div className="px-4 sm:px6 lg:px-6">
          <PageHeading title="Superadmin" />
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {orgs.length} organization{orgs.length !== 1 ? "s" : ""}
            </p>
            <Button
              icon={ArrowPathIcon}
              onClick={reconcileOrgs}
              disabled={isReconciling}
            >
              {isReconciling ? "Reconciling…" : "Reconcile empty accounts"}
            </Button>
          </div>

          {fetchError ? (
            <p className="text-red-500 text-sm font-mono">API error: {fetchError}</p>
          ) : (
            <Table
              data={tableData}
              columns={columns}
              defaultSort="name"
              isLoading={isLoading}
              emptyState={
                <div className="flex flex-col items-center justify-center h-72">
                  <BuildingOffice2Icon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                  <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
                    No organizations found
                  </p>
                </div>
              }
            />
          )}

          {deleteSuccess.displaying && (
            <SuccessToast message={deleteSuccess.message} />
          )}
          {reconcileToast.displaying && (
            <SuccessToast message={reconcileToast.message} />
          )}
        </div>
      </main>

      <DeleteConfirm
        isOpen={isDeleteOpen}
        toggleOpen={() => setIsDeleteOpen(!isDeleteOpen)}
        selectedDeleteItem={selectedDeleteItem}
        handleDeleteSuccess={handleDeleteSuccess}
        onDelete={(id) => deleteOrg(id)}
      />
    </div>
  );
}

export { Superadmin };
